'use client';
import React, { useState, useId } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

// ─── Types ────────────────────────────────────────────────────────────────────

type Region = 'africa' | 'asia' | 'americas' | 'europe' | 'middle_east' | 'oceania';
type Level = 'early_childhood' | 'foundation' | 'elementary' | 'middle_school' | 'high_school' | 'college_prep';

interface QuizQ {
  q: string;
  options: string[];
  correct: number;
  explanation: string;
}

interface LessonContent {
  title: string;
  subtitle: string;
  objectives: string[];
  videoSlot: string;
  activities: string[];
  quiz: QuizQ[];
  standards: string[];
  mentorTip: string;
}

// ─── Region Config ────────────────────────────────────────────────────────────

const REGIONS: Record<Region, {
  label: string;
  flag: string;
  color: string;
  gradient: string;
  mentorName: string;
  mentorSvg: string;
  mentorBg: string;
  standardsBody: string;
  countries: string;
}> = {
  africa: {
    label: 'Africa',
    flag: '🌍',
    color: 'text-amber-700',
    gradient: 'from-amber-500 to-orange-600',
    mentorName: 'Amara',
    mentorSvg: '/assets/mentor-africa.svg',
    mentorBg: 'bg-amber-50',
    standardsBody: 'African Union / NERDC / CAPS / KICD',
    countries: 'Nigeria, Kenya, South Africa, Ghana, Ethiopia & more',
  },
  asia: {
    label: 'Asia',
    flag: '🌏',
    color: 'text-sky-700',
    gradient: 'from-sky-500 to-blue-600',
    mentorName: 'Kai',
    mentorSvg: '/assets/mentor-asia.svg',
    mentorBg: 'bg-sky-50',
    standardsBody: 'MOE Singapore / CBSE India / Chinese National Curriculum',
    countries: 'India, China, Japan, Indonesia, Bangladesh & more',
  },
  americas: {
    label: 'Americas',
    flag: '🌎',
    color: 'text-teal-700',
    gradient: 'from-teal-500 to-emerald-600',
    mentorName: 'Maya',
    mentorSvg: '/assets/mentor-africa.svg',
    mentorBg: 'bg-teal-50',
    standardsBody: 'Common Core (USA) / SEP Mexico / MEC Brazil',
    countries: 'USA, Canada, Brazil, Mexico, Colombia & more',
  },
  europe: {
    label: 'Europe',
    flag: '🇪🇺',
    color: 'text-indigo-700',
    gradient: 'from-indigo-500 to-violet-600',
    mentorName: 'Lena',
    mentorSvg: '/assets/mentor-asia.svg',
    mentorBg: 'bg-indigo-50',
    standardsBody: 'UK National Curriculum / EU Key Competences / PISA',
    countries: 'UK, Germany, France, Netherlands, Sweden & more',
  },
  middle_east: {
    label: 'Middle East',
    flag: '🕌',
    color: 'text-emerald-700',
    gradient: 'from-emerald-500 to-teal-600',
    mentorName: 'Zaid',
    mentorSvg: '/assets/mentor-africa.svg',
    mentorBg: 'bg-emerald-50',
    standardsBody: 'UAE Ministry of Education / Saudi MOE / Qatar MOEHE',
    countries: 'UAE, Saudi Arabia, Qatar, Jordan, Egypt & more',
  },
  oceania: {
    label: 'Oceania',
    flag: '🌊',
    color: 'text-cyan-700',
    gradient: 'from-cyan-500 to-sky-600',
    mentorName: 'Tane',
    mentorSvg: '/assets/mentor-asia.svg',
    mentorBg: 'bg-cyan-50',
    standardsBody: 'Australian Curriculum (ACARA) / NZ Curriculum',
    countries: 'Australia, New Zealand, Fiji, Papua New Guinea & more',
  },
};

const LEVELS: Record<Level, { label: string; ageRange: string; emoji: string; color: string }> = {
  early_childhood: { label: 'Early Childhood', ageRange: '0–5 yrs', emoji: '🍼', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  foundation: { label: 'Foundation', ageRange: '5–7 yrs', emoji: '🌱', color: 'bg-green-100 text-green-700 border-green-200' },
  elementary: { label: 'Elementary', ageRange: '7–11 yrs', emoji: '📚', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  middle_school: { label: 'Middle School', ageRange: '11–14 yrs', emoji: '🔬', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  high_school: { label: 'High School', ageRange: '14–18 yrs', emoji: '🎓', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  college_prep: { label: 'College Prep', ageRange: '17–19 yrs', emoji: '🏛️', color: 'bg-teal-100 text-teal-700 border-teal-200' },
};

// ─── Curriculum Content ───────────────────────────────────────────────────────

const CURRICULUM: Record<Region, Record<Level, LessonContent>> = {
  africa: {
    early_childhood: {
      title: 'Counting with Nature',
      subtitle: 'Numbers 1–10 through African animals and plants',
      objectives: [
        'Count objects from 1 to 10 using natural items',
        'Identify and name 5 African animals',
        'Match quantities to number symbols',
        'Develop fine motor skills through tracing numbers',
      ],
      videoSlot: 'Animated counting song with Amara and African savanna animals',
      activities: [
        '🦁 Count the lions: drag-and-drop lion icons onto numbered fields',
        '🌿 Leaf sorting: group leaves by size and count each group',
        '🥁 Drum beat counting: tap the drum the correct number of times',
        '🎨 Colour and count: colour exactly 3 elephants in a scene',
      ],
      quiz: [
        { q: 'How many giraffes are in the picture?', options: ['2', '3', '4', '5'], correct: 1, explanation: 'There are 3 giraffes. Count each one carefully!' },
        { q: 'Which group has 5 animals?', options: ['🐘🐘', '🦓🦓🦓🦓🦓', '🦁🦁🦁', '🐊🐊🐊🐊'], correct: 1, explanation: 'The zebra group has exactly 5 animals.' },
      ],
      standards: ['NERDC Early Years: Number & Quantity', 'CAPS Foundation Phase: Mathematics', 'African Union ECD Framework 2.1'],
      mentorTip: 'Great job counting! In many African cultures, we count using nature around us. Every leaf, stone, and animal teaches us numbers!',
    },
    foundation: {
      title: 'Shapes in African Architecture',
      subtitle: 'Geometry through traditional African building patterns',
      objectives: [
        'Identify circles, squares, triangles, and rectangles',
        'Describe shapes using sides and corners',
        'Recognise geometric patterns in African art and architecture',
        'Create simple patterns using basic shapes',
      ],
      videoSlot: 'Animated tour of African architecture with Amara pointing out shapes',
      activities: [
        '🏠 Build a mud hut: arrange circles and rectangles to construct a traditional home',
        '🔺 Kente pattern maker: create repeating patterns using triangles and squares',
        '🔍 Shape hunt: identify shapes hidden in a Maasai bead necklace image',
        '✏️ Trace and name: trace shapes found in Ndebele wall paintings',
      ],
      quiz: [
        { q: 'How many sides does a triangle have?', options: ['2', '3', '4', '6'], correct: 1, explanation: 'A triangle has 3 sides and 3 corners.' },
        { q: 'Which shape is like a wheel?', options: ['Square', 'Triangle', 'Circle', 'Rectangle'], correct: 2, explanation: 'A circle is round like a wheel — no corners, no sides!' },
      ],
      standards: ['NERDC Foundation: Shapes & Space', 'CAPS Grade 1 Mathematics', 'KICD Kenya Foundation Curriculum'],
      mentorTip: 'African builders have used shapes for thousands of years! The Great Zimbabwe walls use rectangles, and Nubian pyramids are triangles. Shapes are everywhere!',
    },
    elementary: {
      title: 'African Ecosystems & Food Chains',
      subtitle: 'Life science through the Serengeti and Congo Basin',
      objectives: [
        'Define producer, consumer, and decomposer',
        'Construct a simple food chain with 4 organisms',
        'Explain how energy flows through an ecosystem',
        'Identify threats to African ecosystems and conservation efforts',
      ],
      videoSlot: 'Animated documentary-style lesson on the Serengeti food web with Amara as guide',
      activities: [
        '🌿 Build a food chain: drag organisms into the correct order (grass → zebra → lion → bacteria)',
        '🔬 Ecosystem explorer: click on organisms in an interactive savanna scene to learn their role',
        '📊 Population graph: analyse a bar chart showing predator-prey population cycles',
        '🌍 Conservation pledge: design a poster to protect an endangered African species',
      ],
      quiz: [
        { q: 'What is a producer in a food chain?', options: ['An animal that eats plants', 'A plant that makes its own food', 'An animal that eats other animals', 'A fungus that breaks down matter'], correct: 1, explanation: 'Producers like grass and trees make their own food using sunlight through photosynthesis.' },
        { q: 'What happens if lions disappear from the Serengeti?', options: ['More grass grows', 'Zebra populations increase uncontrollably', 'Nothing changes', 'Plants die'], correct: 1, explanation: 'Without lions, zebra and wildebeest populations would grow too large, overgrazing the land.' },
      ],
      standards: ['NERDC Basic Science Grade 4–6', 'CAPS Natural Sciences Grade 5', 'KICD Science Strand 3.2'],
      mentorTip: 'Africa has 25% of the world\'s biodiversity! From the Congo rainforest to the Sahara, every ecosystem is a classroom. Protecting nature is a form of worship.',
    },
    middle_school: {
      title: 'African History: Great Empires & Trade Routes',
      subtitle: 'Mali, Songhai, Great Zimbabwe, and the Trans-Saharan Trade',
      objectives: [
        'Identify the major African empires from 300 CE to 1600 CE',
        'Analyse how trade routes shaped African civilisations',
        'Evaluate primary sources from Mansa Musa\'s pilgrimage',
        'Compare governance systems of Mali and Songhai empires',
      ],
      videoSlot: 'Animated historical documentary on Mansa Musa\'s pilgrimage with Amara narrating',
      activities: [
        '🗺️ Map the trade routes: draw Trans-Saharan and Indian Ocean trade paths on an interactive map',
        '📜 Primary source analysis: read an excerpt from Ibn Battuta\'s account of Mali',
        '⚖️ Compare & contrast: Venn diagram of Mali vs Songhai governance',
        '💰 Economics simulation: manage a virtual gold-salt trade caravan',
      ],
      quiz: [
        { q: 'Who was Mansa Musa?', options: ['A warrior king of Songhai', 'The richest ruler of Mali Empire', 'A Swahili coast merchant', 'A Nubian pharaoh'], correct: 1, explanation: 'Mansa Musa ruled the Mali Empire (1312–1337) and was one of the wealthiest people in history.' },
        { q: 'What were the two main goods traded across the Sahara?', options: ['Oil and cotton', 'Gold and salt', 'Ivory and spices', 'Copper and iron'], correct: 1, explanation: 'Gold from West Africa and salt from the Sahara were the foundation of Trans-Saharan trade.' },
      ],
      standards: ['NERDC Social Studies Grade 7–9', 'CAPS History Grade 8', 'African Union Curriculum Framework — History'],
      mentorTip: 'Africa\'s history is rich and powerful. Timbuktu had universities when Europe was in the Dark Ages. Your heritage is a source of pride and knowledge!',
    },
    high_school: {
      title: 'African Political Economy & Development',
      subtitle: 'Post-colonial governance, the African Union, and Agenda 2063',
      objectives: [
        'Analyse the causes and effects of colonialism on African economies',
        'Evaluate the role of the African Union in regional integration',
        'Assess Agenda 2063 goals against current development indicators',
        'Construct an evidence-based argument on a contemporary African policy issue',
      ],
      videoSlot: 'Documentary-style lesson on African Union institutions with Amara as analyst',
      activities: [
        '📊 Data analysis: compare GDP, HDI, and literacy rates across 10 African nations',
        '🗣️ Debate simulation: argue for or against free trade under AfCFTA',
        '📝 Policy brief: write a 400-word brief on one Agenda 2063 goal',
        '🌐 Case study: analyse Rwanda\'s economic transformation 1994–2024',
      ],
      quiz: [
        { q: 'What is Agenda 2063?', options: ['An AU military alliance', 'Africa\'s 50-year development blueprint', 'A colonial-era trade agreement', 'A UN climate framework'], correct: 1, explanation: 'Agenda 2063 is the African Union\'s strategic framework for transforming Africa into a global powerhouse by 2063.' },
        { q: 'What does AfCFTA stand for?', options: ['African Free Commerce and Trade Alliance', 'African Continental Free Trade Area', 'African Central Financial Trade Authority', 'African Cooperative Free Trade Agreement'], correct: 1, explanation: 'AfCFTA is the African Continental Free Trade Area, creating the world\'s largest free trade zone by number of countries.' },
      ],
      standards: ['NERDC Economics/Government Grade 10–12', 'CAPS Economics Grade 11', 'IB Global Politics Africa Focus'],
      mentorTip: 'Africa is the future. With the youngest population on Earth and vast natural resources, understanding African political economy is essential for any global leader.',
    },
    college_prep: {
      title: 'African Literature & Postcolonial Theory',
      subtitle: 'Chinua Achebe, Ngugi wa Thiong\'o, and the politics of language',
      objectives: [
        'Analyse Things Fall Apart through a postcolonial lens',
        'Evaluate Ngugi\'s argument for decolonising the mind',
        'Apply Said\'s Orientalism framework to African representation',
        'Produce a comparative literary essay at university standard',
      ],
      videoSlot: 'Lecture-style analysis of postcolonial African literature with Amara as professor',
      activities: [
        '📖 Close reading: annotate a passage from Things Fall Apart for colonial themes',
        '🖊️ Essay outline: structure a comparative essay on two African novels',
        '🎙️ Socratic seminar: discuss "Is writing in English a form of cultural surrender?"',
        '📚 Research task: find three scholarly sources on African postcolonial theory',
      ],
      quiz: [
        { q: 'What is the central theme of Things Fall Apart?', options: ['The beauty of Igbo culture', 'The destructive impact of colonialism on traditional society', 'A love story set in Nigeria', 'The rise of African nationalism'], correct: 1, explanation: 'Achebe\'s novel explores how colonialism dismantled Igbo society, culture, and identity.' },
        { q: 'What does Ngugi argue in "Decolonising the Mind"?', options: ['African languages are inferior', 'Writing in African languages is essential for cultural liberation', 'English is the best language for African literature', 'Colonialism had no lasting effects'], correct: 1, explanation: 'Ngugi argues that language is the carrier of culture, and writing in African languages is an act of decolonisation.' },
      ],
      standards: ['IB Language A Literature HL', 'A-Level English Literature (AQA)', 'AP Literature & Composition'],
      mentorTip: 'African literature is not just stories — it is resistance, identity, and reclamation. Reading Achebe and Ngugi is reading the soul of a continent.',
    },
  },
  asia: {
    early_childhood: {
      title: 'Colours of Asia',
      subtitle: 'Learning colours through Asian festivals and nature',
      objectives: [
        'Identify and name 8 basic colours',
        'Match colours to objects from Asian cultures',
        'Sort objects by colour into groups',
        'Express colour preferences through creative activities',
      ],
      videoSlot: 'Animated colour song with Kai exploring Diwali lights and cherry blossoms',
      activities: [
        '🏮 Lantern colouring: colour a Chinese New Year lantern using the correct colours',
        '🌸 Cherry blossom sort: drag pink, white, and red petals into the correct baskets',
        '🎨 Rangoli maker: create a Diwali rangoli pattern using 5 colours',
        '🌈 Rainbow match: match coloured objects to their colour name cards',
      ],
      quiz: [
        { q: 'What colour is a ripe mango?', options: ['Blue', 'Yellow', 'Purple', 'Green'], correct: 1, explanation: 'A ripe mango is yellow-orange! Mangoes are a favourite fruit across Asia.' },
        { q: 'What colour are cherry blossoms?', options: ['Red', 'Blue', 'Pink', 'Yellow'], correct: 2, explanation: 'Cherry blossoms are pink! They bloom in spring in Japan and are celebrated with festivals.' },
      ],
      standards: ['MOE Singapore Kindergarten Framework', 'CBSE India Pre-Primary', 'Japanese MEXT Early Childhood'],
      mentorTip: 'Asia is full of beautiful colours! From the red lanterns of China to the colourful saris of India, colours tell stories about our cultures.',
    },
    foundation: {
      title: 'Numbers & Patterns in Asian Art',
      subtitle: 'Mathematics through mandalas, origami, and tile patterns',
      objectives: [
        'Count to 20 using structured patterns',
        'Identify repeating patterns (AB, ABC, AABB)',
        'Recognise symmetry in Asian art forms',
        'Create a simple repeating pattern',
      ],
      videoSlot: 'Animated origami lesson with Kai folding shapes and counting steps',
      activities: [
        '🦢 Origami counter: fold paper and count each fold step (1–10)',
        '🔷 Mandala pattern: complete a symmetrical mandala by identifying the repeating pattern',
        '🎋 Bamboo counting: count bamboo stalks grouped in 5s and 10s',
        '🧩 Tile puzzle: arrange tiles to create an ABAB pattern',
      ],
      quiz: [
        { q: 'What comes next in the pattern: 🔴🔵🔴🔵___?', options: ['🔴', '🔵', '🟡', '🟢'], correct: 0, explanation: 'The pattern is red-blue-red-blue, so the next is red!' },
        { q: 'How many folds does it take to make a basic origami crane? (Count: 1,2,3,4,5)', options: ['3', '4', '5', '6'], correct: 2, explanation: 'We counted 5 folds! Counting carefully helps us follow instructions.' },
      ],
      standards: ['MOE Singapore Primary 1 Mathematics', 'CBSE Grade 1 Maths', 'Korean NEIS Foundation'],
      mentorTip: 'Patterns are everywhere in Asian art! Mandalas, batik fabric, and temple tiles all use repeating patterns. Maths is beautiful!',
    },
    elementary: {
      title: 'Asian Civilisations: Rivers & Early Cities',
      subtitle: 'Indus Valley, Yellow River, and Mesopotamia connections',
      objectives: [
        'Identify the four major early river civilisations',
        'Explain why rivers were essential for early settlements',
        'Compare features of Mohenjo-daro and ancient Chinese cities',
        'Analyse archaeological evidence to draw conclusions',
      ],
      videoSlot: 'Animated archaeological dig with Kai uncovering Indus Valley artefacts',
      activities: [
        '🗺️ River map: label the Indus, Yellow, Tigris, and Nile rivers on an interactive map',
        '🏺 Artefact analysis: examine images of Indus Valley seals and describe what they tell us',
        '🏙️ City builder: design a river city with water access, walls, and granaries',
        '📜 Timeline: place 6 early civilisations on a chronological timeline',
      ],
      quiz: [
        { q: 'Why did early civilisations develop near rivers?', options: ['Rivers were beautiful', 'Rivers provided water, fertile soil, and transport', 'Rivers kept enemies away', 'Rivers had fish only'], correct: 1, explanation: 'Rivers provided fresh water for drinking and farming, fertile silt for crops, and routes for trade and travel.' },
        { q: 'Which civilisation developed in the Indus Valley?', options: ['Egyptian', 'Mesopotamian', 'Harappan', 'Chinese'], correct: 2, explanation: 'The Harappan (Indus Valley) civilisation flourished in modern-day Pakistan and India around 2500 BCE.' },
      ],
      standards: ['CBSE Social Science Grade 6', 'MOE Singapore History Grade 5', 'Japanese MEXT Social Studies'],
      mentorTip: 'Asia is the birthplace of some of the world\'s oldest civilisations! The Indus Valley had indoor plumbing 4,000 years ago. Your ancestors were brilliant engineers!',
    },
    middle_school: {
      title: 'Asia\'s Economic Rise: From Agriculture to Industry',
      subtitle: 'The Asian Tigers, China\'s growth, and India\'s digital economy',
      objectives: [
        'Explain the factors behind the Asian economic miracle',
        'Analyse GDP growth data for South Korea, Singapore, China, and India',
        'Evaluate the social costs and benefits of rapid industrialisation',
        'Predict future trends in the Asian digital economy',
      ],
      videoSlot: 'Data-driven documentary on Asian economic growth with Kai as economist',
      activities: [
        '📊 Graph analysis: interpret GDP growth charts for the Asian Tigers (1960–2020)',
        '🏭 Factory simulation: manage a virtual manufacturing plant balancing profit and worker welfare',
        '🌐 Digital economy map: identify Asia\'s top tech companies and their global reach',
        '📝 Case study: compare South Korea\'s Samsung with India\'s Tata Group',
      ],
      quiz: [
        { q: 'Which countries are known as the "Asian Tigers"?', options: ['China, India, Japan, Vietnam', 'South Korea, Taiwan, Singapore, Hong Kong', 'Thailand, Malaysia, Indonesia, Philippines', 'Pakistan, Bangladesh, Sri Lanka, Nepal'], correct: 1, explanation: 'The Four Asian Tigers — South Korea, Taiwan, Singapore, and Hong Kong — achieved rapid industrialisation from the 1960s–90s.' },
        { q: 'What is a key driver of India\'s modern economy?', options: ['Oil exports', 'IT and software services', 'Agricultural exports', 'Tourism only'], correct: 1, explanation: 'India\'s IT sector, centred in Bangalore, is a global powerhouse generating over $200 billion annually.' },
      ],
      standards: ['CBSE Economics Grade 9', 'MOE Singapore Social Studies Grade 8', 'IB MYP Individuals & Societies'],
      mentorTip: 'Asia produces over 40% of the world\'s GDP. Understanding Asian economics is understanding the future of the global economy. You are part of this story!',
    },
    high_school: {
      title: 'Physics: Quantum Mechanics Foundations',
      subtitle: 'Wave-particle duality, the uncertainty principle, and quantum applications',
      objectives: [
        'Explain wave-particle duality with experimental evidence',
        'Apply Heisenberg\'s uncertainty principle to real scenarios',
        'Describe how quantum mechanics underlies modern technology',
        'Evaluate the philosophical implications of quantum indeterminacy',
      ],
      videoSlot: 'Animated quantum physics explainer with Kai conducting the double-slit experiment',
      activities: [
        '🔬 Virtual double-slit experiment: observe interference patterns and adjust variables',
        '📐 Uncertainty calculations: apply ΔxΔp ≥ ℏ/2 to solve problems',
        '💻 Quantum tech timeline: map quantum mechanics discoveries to modern technologies',
        '🤔 Philosophy debate: discuss "Does observation create reality?" using quantum evidence',
      ],
      quiz: [
        { q: 'What does wave-particle duality mean?', options: ['Waves and particles are different things', 'Light and matter exhibit both wave and particle properties', 'Only light has wave properties', 'Particles are made of waves'], correct: 1, explanation: 'Wave-particle duality means quantum objects like electrons and photons behave as both waves and particles depending on how they are observed.' },
        { q: 'What does Heisenberg\'s uncertainty principle state?', options: ['Energy is always conserved', 'You cannot know both position and momentum precisely simultaneously', 'Light travels at constant speed', 'Electrons orbit the nucleus'], correct: 1, explanation: 'The uncertainty principle states that the more precisely you know a particle\'s position, the less precisely you can know its momentum, and vice versa.' },
      ],
      standards: ['CBSE Physics Grade 12 Chapter 11', 'MOE Singapore A-Level Physics', 'IB Physics HL Topic 12'],
      mentorTip: 'Asian scientists like C.V. Raman, Satyendra Nath Bose, and Abdus Salam made foundational contributions to quantum physics. You stand on giants\' shoulders!',
    },
    college_prep: {
      title: 'Comparative Asian Political Systems',
      subtitle: 'Democracy, authoritarianism, and hybrid regimes in Asia',
      objectives: [
        'Compare democratic and authoritarian governance models in Asia',
        'Analyse the relationship between economic development and political freedom',
        'Evaluate competing theories of Asian democracy',
        'Construct a research-quality comparative political analysis',
      ],
      videoSlot: 'Lecture-style comparative politics seminar with Kai as political scientist',
      activities: [
        '📊 Freedom Index analysis: compare Freedom House scores for 10 Asian nations',
        '🗳️ Case study: analyse Japan\'s parliamentary democracy vs China\'s one-party system',
        '📝 Research essay: `Is the "Asian Values" argument a justification for authoritarianism?`',
        '🌐 Policy simulation: design governance reforms for a fictional Asian nation',
      ],
      quiz: [
        { q: 'What is the "developmental state" model associated with?', options: ['Laissez-faire capitalism', 'State-directed economic development with limited democracy', 'Communist central planning', 'Federal democratic systems'], correct: 1, explanation: 'The developmental state model, seen in South Korea, Taiwan, and Singapore, involves strong state direction of the economy alongside limited political pluralism.' },
        { q: 'Which Asian country has the oldest continuous democratic system?', options: ['China', 'India', 'Japan', 'South Korea'], correct: 1, explanation: 'India has been a continuous democracy since independence in 1947, making it the world\'s largest democracy.' },
      ],
      standards: ['IB Global Politics HL', 'AP Comparative Government', 'A-Level Politics (Edexcel)'],
      mentorTip: 'Asia\'s political diversity is a laboratory for political science. From India\'s vibrant democracy to Singapore\'s technocracy, every system offers lessons for the world.',
    },
  },
  americas: {
    early_childhood: {
      title: 'Animals of the Americas',
      subtitle: 'Learning about North and South American wildlife',
      objectives: [
        'Name 6 animals native to the Americas',
        'Distinguish between land, water, and air animals',
        'Match animals to their habitats',
        'Develop vocabulary for animal characteristics',
      ],
      videoSlot: 'Animated nature walk with Maya through the Amazon rainforest and North American prairies',
      activities: [
        '🦅 Eagle sorting: sort animals by where they live (land/water/air)',
        '🐊 Amazon explorer: click on animals in a rainforest scene to learn their names',
        '🦬 Buffalo count: count bison on a prairie and match to number cards',
        '🎨 Animal mask: colour a traditional Indigenous animal mask',
      ],
      quiz: [
        { q: 'Where does a jaguar live?', options: ['Arctic tundra', 'Rainforest', 'Desert', 'Ocean'], correct: 1, explanation: 'Jaguars live in the rainforests of South America, especially the Amazon basin.' },
        { q: 'Which animal can fly?', options: ['Jaguar', 'Anaconda', 'Eagle', 'Capybara'], correct: 2, explanation: 'Eagles are birds that can fly! The bald eagle is the national symbol of the USA.' },
      ],
      standards: ['Common Core ELA-Literacy K.RI.1', 'Head Start Child Development Standards', 'SEP Mexico Preescolar'],
      mentorTip: 'The Americas are home to the Amazon — the world\'s largest rainforest! It has more animal species than anywhere else on Earth. Let\'s protect it together!',
    },
    foundation: {
      title: 'Addition & Subtraction Stories',
      subtitle: 'Number operations through everyday American life contexts',
      objectives: [
        'Add numbers up to 20 using counting strategies',
        'Subtract numbers up to 20 with understanding',
        'Solve simple word problems with addition and subtraction',
        'Represent operations with objects and drawings',
      ],
      videoSlot: 'Animated story with Maya at a farmers\' market solving addition problems',
      activities: [
        '🍎 Fruit stand: add apples and oranges to find the total',
        '🎒 Backpack packing: subtract items taken out of a backpack',
        '🏀 Basketball score: add points scored in each quarter',
        '🌽 Corn harvest: solve word problems about harvesting corn',
      ],
      quiz: [
        { q: 'Maya has 7 apples. She gives 3 to her friend. How many does she have left?', options: ['3', '4', '5', '10'], correct: 1, explanation: '7 - 3 = 4. Maya has 4 apples left.' },
        { q: 'There are 5 red birds and 6 blue birds. How many birds altogether?', options: ['10', '11', '12', '9'], correct: 1, explanation: '5 + 6 = 11 birds altogether.' },
      ],
      standards: ['Common Core Math K.OA.A.1', 'TEKS Grade 1 Mathematics', 'SEP Mexico Primaria'],
      mentorTip: 'Maths is everywhere! When you share food, count money, or keep score in a game, you are doing maths. You are already a mathematician!',
    },
    elementary: {
      title: 'American Revolution: Causes & Consequences',
      subtitle: 'From colonial grievances to the Declaration of Independence',
      objectives: [
        'Identify the main causes of the American Revolution',
        'Explain the significance of the Declaration of Independence',
        'Analyse the perspectives of Patriots, Loyalists, and enslaved people',
        'Evaluate the long-term impact of the Revolution on democracy',
      ],
      videoSlot: 'Animated historical drama with Maya witnessing the Boston Tea Party',
      activities: [
        '📜 Document analysis: read excerpts from the Declaration of Independence',
        '🎭 Perspective role-play: write a diary entry as a Patriot, Loyalist, or enslaved person',
        '🗺️ Battle map: trace the major battles of the Revolutionary War',
        '⚖️ Cause & effect chart: link colonial grievances to revolutionary actions',
      ],
      quiz: [
        { q: 'What was the main complaint of colonists before the Revolution?', options: ['The weather was bad', 'Taxation without representation', 'They wanted a king', 'They disliked farming'], correct: 1, explanation: 'Colonists objected to being taxed by the British Parliament without having elected representatives there — "no taxation without representation."' },
        { q: 'When was the Declaration of Independence signed?', options: ['1776', '1789', '1812', '1763'], correct: 0, explanation: 'The Declaration of Independence was adopted on July 4, 1776.' },
      ],
      standards: ['Common Core History Grade 5', 'TEKS Social Studies Grade 5', 'C3 Framework D2.His.1.3-5'],
      mentorTip: 'The American Revolution changed the world! The idea that all people have rights that governments cannot take away inspired revolutions across the globe.',
    },
    middle_school: {
      title: 'Algebra: Linear Equations & Real-World Applications',
      subtitle: 'Solving equations and graphing lines in context',
      objectives: [
        'Solve one- and two-step linear equations',
        'Graph linear equations on a coordinate plane',
        'Interpret slope and y-intercept in real-world contexts',
        'Write equations to model real-world situations',
      ],
      videoSlot: 'Animated algebra lesson with Maya solving real-world problems using equations',
      activities: [
        '📱 Phone plan comparison: write and solve equations to compare two phone plans',
        '📈 Graph the line: plot points and draw lines for given equations',
        '🚗 Distance-time problems: write equations for road trip scenarios',
        '🏪 Business model: use linear equations to model a small business\'s profit',
      ],
      quiz: [
        { q: 'Solve: 2x + 5 = 13', options: ['x = 3', 'x = 4', 'x = 9', 'x = 6'], correct: 1, explanation: '2x + 5 = 13 → 2x = 8 → x = 4. Check: 2(4) + 5 = 13 ✓' },
        { q: 'What does the slope of a line represent?', options: ['The starting point', 'The rate of change', 'The maximum value', 'The x-intercept'], correct: 1, explanation: 'Slope represents the rate of change — how much y changes for every 1 unit increase in x.' },
      ],
      standards: ['Common Core Math 8.EE.C.7', 'TEKS Algebra I', 'CCSS Grade 8 Functions'],
      mentorTip: 'Algebra is the language of problem-solving! Engineers, scientists, economists, and programmers all use algebra every day. Master it and you can solve anything.',
    },
    high_school: {
      title: 'AP Chemistry: Thermodynamics & Equilibrium',
      subtitle: 'Enthalpy, entropy, Gibbs free energy, and Le Chatelier\'s principle',
      objectives: [
        'Calculate enthalpy changes using Hess\'s Law',
        'Apply the second law of thermodynamics to predict spontaneity',
        'Calculate Gibbs free energy and interpret its sign',
        'Apply Le Chatelier\'s principle to predict equilibrium shifts',
      ],
      videoSlot: 'Virtual lab demonstration with Maya conducting thermodynamic experiments',
      activities: [
        '🧪 Virtual calorimetry: measure heat transfer in a virtual coffee-cup calorimeter',
        '📐 Hess\'s Law calculations: use given reactions to calculate ΔH for a target reaction',
        '⚖️ Equilibrium simulation: adjust concentrations and observe Le Chatelier shifts',
        '📊 ΔG analysis: calculate and interpret Gibbs free energy for 5 reactions',
      ],
      quiz: [
        { q: 'A reaction has ΔH < 0 and ΔS > 0. What can you conclude?', options: ['Non-spontaneous at all temperatures', 'Spontaneous at all temperatures', 'Spontaneous only at high temperatures', 'Spontaneous only at low temperatures'], correct: 1, explanation: 'When ΔH < 0 (exothermic) and ΔS > 0 (entropy increases), ΔG = ΔH - TΔS is always negative, so the reaction is spontaneous at all temperatures.' },
        { q: 'Le Chatelier\'s principle states that if a system at equilibrium is disturbed, it will:', options: ['Stop reacting', 'Shift to counteract the disturbance', 'Shift to increase the disturbance', 'Reach a new temperature'], correct: 1, explanation: 'Le Chatelier\'s principle: a system at equilibrium shifts in the direction that partially counteracts any imposed change.' },
      ],
      standards: ['AP Chemistry Big Idea 5', 'Common Core Science HS-PS1-4', 'Next Generation Science Standards'],
      mentorTip: 'Thermodynamics explains why ice melts, why engines work, and why life itself is possible. Chemistry is the science of transformation!',
    },
    college_prep: {
      title: 'SAT/ACT Prep: Critical Reading & Evidence-Based Writing',
      subtitle: 'Advanced reading strategies, rhetoric analysis, and essay construction',
      objectives: [
        'Apply close reading strategies to complex informational texts',
        'Identify rhetorical devices and their persuasive effects',
        'Construct a well-supported argumentative essay in 50 minutes',
        'Demonstrate command of standard English conventions',
      ],
      videoSlot: 'Test prep masterclass with Maya breaking down SAT reading passages',
      activities: [
        '📖 Timed reading: complete a full SAT reading passage in 13 minutes with annotation',
        '✏️ Rhetoric analysis: identify ethos, pathos, and logos in a political speech',
        '📝 Essay sprint: write a full argumentative essay from prompt to conclusion in 50 minutes',
        '🔍 Grammar drill: identify and correct 20 standard English convention errors',
      ],
      quiz: [
        { q: 'What is the primary purpose of a thesis statement?', options: ['To summarise the entire essay', 'To state the main argument and preview supporting points', 'To provide background information', 'To conclude the essay'], correct: 1, explanation: 'A thesis statement presents the central argument and typically previews the main supporting points of the essay.' },
        { q: 'Which rhetorical appeal relies on the speaker\'s credibility?', options: ['Pathos', 'Logos', 'Ethos', 'Kairos'], correct: 2, explanation: 'Ethos is the appeal to credibility or character — establishing why the audience should trust the speaker.' },
      ],
      standards: ['College Board SAT Evidence-Based Reading', 'ACT English & Reading', 'Common Core ELA Grade 12'],
      mentorTip: 'The SAT and ACT are not tests of intelligence — they are tests of skill. With the right strategies and practice, you can master them. Let\'s get you that score!',
    },
  },
  europe: {
    early_childhood: {
      title: 'Seasons & Weather in Europe',
      subtitle: 'Science and vocabulary through European seasons',
      objectives: [
        'Name the four seasons and their characteristics',
        'Describe typical weather in each season',
        'Match clothing and activities to appropriate seasons',
        'Observe and record simple weather data',
      ],
      videoSlot: 'Animated seasons journey with Lena through a European village across all four seasons',
      activities: [
        '☀️ Season sort: drag clothing items to the correct season',
        '🌧️ Weather chart: record today\'s weather using symbols',
        '🍂 Leaf collection: identify autumn leaves from different European trees',
        '⛄ Winter scene: build a snowman and label winter weather words',
      ],
      quiz: [
        { q: 'In which season do leaves fall from trees?', options: ['Spring', 'Summer', 'Autumn', 'Winter'], correct: 2, explanation: 'In autumn, deciduous trees shed their leaves as days get shorter and temperatures drop.' },
        { q: 'What do you wear in winter?', options: ['T-shirt and shorts', 'Coat, hat, and gloves', 'Swimsuit', 'Light dress'], correct: 1, explanation: 'In winter we wear warm clothes like coats, hats, and gloves to stay warm in cold weather.' },
      ],
      standards: ['UK EYFS Understanding the World', 'German Bildungsplan Kindergarten', 'French Maternelle Programme'],
      mentorTip: 'Europe has four beautiful seasons! Each one brings different plants, animals, and activities. Observing nature is the first step to being a scientist.',
    },
    foundation: {
      title: 'Phonics & Early Reading',
      subtitle: 'Systematic synthetic phonics for English literacy',
      objectives: [
        'Decode CVC words using phoneme-grapheme correspondences',
        'Blend sounds to read simple words',
        'Segment words into phonemes for spelling',
        'Read simple sentences with high-frequency words',
      ],
      videoSlot: 'Animated phonics lesson with Lena sounding out words in a storybook setting',
      activities: [
        '🔤 Sound buttons: tap each sound in a word and blend to read it',
        '🎵 Phonics song: sing the alphabet sounds with Lena',
        '📖 Decodable reader: read a 6-page decodable book with CVC words',
        '✏️ Spelling practice: listen to a word and drag letters to spell it',
      ],
      quiz: [
        { q: 'What word do these sounds make? /c/ /a/ /t/', options: ['cut', 'cat', 'cot', 'kit'], correct: 1, explanation: 'Blending /c/ + /a/ + /t/ makes "cat"! Well done for blending those sounds.' },
        { q: 'How many sounds are in the word "ship"?', options: ['4', '3', '2', '5'], correct: 1, explanation: '"Ship" has 3 sounds: /sh/ /i/ /p/. The "sh" is one sound even though it uses two letters.' },
      ],
      standards: ['UK National Curriculum KS1 English', 'Letters and Sounds Phase 2–3', 'Scottish Curriculum for Excellence Early Level'],
      mentorTip: 'Reading is a superpower! Once you can decode sounds, you can read any word in the English language. Keep practising — you are doing brilliantly!',
    },
    elementary: {
      title: 'European History: Ancient Greece & Rome',
      subtitle: 'Democracy, philosophy, and the foundations of Western civilisation',
      objectives: [
        'Describe the key features of Athenian democracy',
        'Identify major contributions of ancient Greece and Rome',
        'Compare Greek and Roman governance systems',
        'Explain how ancient civilisations influence modern Europe',
      ],
      videoSlot: 'Animated historical journey with Lena visiting ancient Athens and Rome',
      activities: [
        '🏛️ Virtual Acropolis tour: explore the Parthenon and learn about Greek gods',
        '🗳️ Democracy simulation: participate in a mock Athenian assembly vote',
        '🗺️ Roman Empire map: trace the expansion of Rome across Europe',
        '📜 Latin roots: identify English words derived from Latin',
      ],
      quiz: [
        { q: 'What type of government did ancient Athens develop?', options: ['Monarchy', 'Democracy', 'Oligarchy', 'Theocracy'], correct: 1, explanation: 'Athens developed democracy — rule by the people. Citizens voted directly on laws and policies in the Assembly.' },
        { q: 'Which structure did the Romans build across their empire?', options: ['Pyramids', 'Castles', 'Roads and aqueducts', 'Mosques'], correct: 2, explanation: 'Romans built an extensive road network and aqueducts to supply water to cities — engineering marvels still visible today.' },
      ],
      standards: ['UK National Curriculum History KS2', 'German Lehrplan Geschichte', 'French Programme Histoire Cycle 3'],
      mentorTip: 'The word "democracy" comes from Greek: demos (people) + kratos (power). Every time you vote or have a say in decisions, you are using an idea invented 2,500 years ago!',
    },
    middle_school: {
      title: 'The Scientific Revolution & Enlightenment',
      subtitle: 'Copernicus, Newton, Locke, and the birth of modern thought',
      objectives: [
        'Explain the key ideas of the Scientific Revolution',
        'Analyse how Enlightenment philosophy challenged traditional authority',
        'Evaluate the impact of Newton\'s laws on scientific thinking',
        'Connect Enlightenment ideas to modern democratic principles',
      ],
      videoSlot: 'Animated intellectual history with Lena meeting Galileo, Newton, and Voltaire',
      activities: [
        '🔭 Galileo\'s telescope: simulate observations that challenged geocentrism',
        '🍎 Newton\'s laws: conduct virtual experiments demonstrating the three laws of motion',
        '📚 Enlightenment debate: argue for or against Locke\'s social contract theory',
        '🗺️ Ideas map: trace how Enlightenment ideas spread from France across Europe',
      ],
      quiz: [
        { q: 'What did Copernicus propose that was revolutionary?', options: ['The Earth is flat', 'The Sun is at the centre of the solar system', 'The Moon causes tides', 'Gravity pulls objects down'], correct: 1, explanation: 'Copernicus proposed the heliocentric model — that the Earth and planets orbit the Sun, not the other way around.' },
        { q: 'What is Locke\'s "social contract"?', options: ['A business agreement', 'The idea that government authority comes from the consent of the governed', 'A religious covenant', 'A trade agreement between nations'], correct: 1, explanation: 'Locke argued that governments derive their authority from the consent of the people, and citizens have the right to overthrow unjust governments.' },
      ],
      standards: ['UK National Curriculum History KS3', 'IB MYP Individuals & Societies', 'German Gymnasium Geschichte'],
      mentorTip: 'The Enlightenment gave us science, democracy, and human rights. Every time you question authority and demand evidence, you are an Enlightenment thinker!',
    },
    high_school: {
      title: 'A-Level Mathematics: Calculus & Differential Equations',
      subtitle: 'Differentiation, integration, and applications to real-world problems',
      objectives: [
        'Differentiate functions using the chain, product, and quotient rules',
        'Integrate functions using substitution and integration by parts',
        'Solve first-order differential equations by separation of variables',
        'Apply calculus to optimisation and rate-of-change problems',
      ],
      videoSlot: 'Step-by-step calculus masterclass with Lena solving A-Level exam questions',
      activities: [
        '📐 Differentiation drill: differentiate 10 functions using all three rules',
        '∫ Integration practice: evaluate definite integrals with substitution',
        '📈 Optimisation problem: find the maximum volume of a box with given surface area',
        '🔬 Differential equations: model population growth using dP/dt = kP',
      ],
      quiz: [
        { q: 'What is the derivative of sin(x)?', options: ['-cos(x)', 'cos(x)', '-sin(x)', 'tan(x)'], correct: 1, explanation: 'd/dx[sin(x)] = cos(x). This is a fundamental derivative you must memorise.' },
        { q: 'What does integration represent geometrically?', options: ['The slope of a curve', 'The area under a curve', 'The maximum of a function', 'The rate of change'], correct: 1, explanation: 'Definite integration calculates the area between a curve and the x-axis over a given interval.' },
      ],
      standards: ['A-Level Mathematics (Edexcel/AQA/OCR)', 'IB Mathematics HL', 'French Baccalauréat Mathématiques'],
      mentorTip: 'Calculus was invented independently by Newton and Leibniz in the 17th century. It is the mathematics of change — and change is what the universe is made of!',
    },
    college_prep: {
      title: 'Extended Essay: Research Methodology & Academic Writing',
      subtitle: 'IB Extended Essay and university-level research skills',
      objectives: [
        'Formulate a focused, researchable research question',
        'Conduct a systematic literature review',
        'Apply appropriate research methodology for the subject area',
        'Write a 4,000-word academic essay with proper citation',
      ],
      videoSlot: 'Research methodology masterclass with Lena guiding through the IB Extended Essay process',
      activities: [
        '🔍 Research question workshop: refine a broad topic into a focused, answerable question',
        '📚 Literature review: find and annotate 5 academic sources using Google Scholar',
        '📝 Outline builder: create a detailed essay outline with argument structure',
        '✏️ Citation practice: format 10 sources in both MLA and APA styles',
      ],
      quiz: [
        { q: 'What makes a good research question?', options: ['It is very broad and general', 'It is focused, specific, and answerable through research', 'It has an obvious answer', 'It requires no evidence'], correct: 1, explanation: 'A good research question is focused enough to be answered within the word limit, specific enough to guide research, and open enough to require investigation.' },
        { q: 'What is a literature review?', options: ['A review of fiction books', 'A critical analysis of existing research on your topic', 'A summary of your own findings', 'A list of books you plan to read'], correct: 1, explanation: 'A literature review critically analyses existing scholarship on your topic, identifying what is known, what is debated, and what gaps your research addresses.' },
      ],
      standards: ['IB Extended Essay Guide', 'A-Level EPQ (AQA)', 'French TPE Travaux Personnels Encadrés'],
      mentorTip: 'The Extended Essay is your first taste of real academic research. The skills you develop — questioning, researching, arguing — are the skills of a scholar for life.',
    },
  },
  middle_east: {
    early_childhood: {
      title: 'Arabic Letters & Sounds',
      subtitle: 'Learning the Arabic alphabet through pictures and stories',
      objectives: [
        'Recognise and name 6 Arabic letters (ا ب ت ث ج ح)',
        'Associate each letter with a familiar word and picture',
        'Trace the basic forms of 6 Arabic letters',
        'Develop phonological awareness in Arabic',
      ],
      videoSlot: 'Animated Arabic alphabet song with Zaid and colourful illustrated characters',
      activities: [
        '✏️ Letter tracing: trace ا ب ت on dotted lines with finger or stylus',
        '🖼️ Picture match: match each letter to its corresponding picture (أسد، بطة، تفاحة)',
        '🎵 Alphabet song: sing along with the Arabic alphabet song',
        '🔤 Letter hunt: find hidden Arabic letters in a colourful scene',
      ],
      quiz: [
        { q: 'Which letter does أسد (lion) start with?', options: ['ب', 'ا', 'ت', 'ج'], correct: 1, explanation: 'أسد starts with the letter ا (Alif). Alif is the first letter of the Arabic alphabet!' },
        { q: 'How many letters are in the Arabic alphabet?', options: ['26', '28', '30', '22'], correct: 1, explanation: 'The Arabic alphabet has 28 letters. It is written from right to left.' },
      ],
      standards: ['UAE MOE KG Arabic Language', 'Saudi MOE Early Childhood Arabic', 'Qatar MOEHE Foundation Arabic'],
      mentorTip: 'Arabic is the language of the Quran — the most beautiful language in the world! Every letter you learn brings you closer to understanding Allah\'s words. ما شاء الله!',
    },
    foundation: {
      title: 'Islamic Values in Daily Life',
      subtitle: 'Honesty, kindness, and gratitude through stories and activities',
      objectives: [
        'Identify and explain 4 core Islamic values: honesty, kindness, gratitude, and patience',
        'Connect Islamic values to everyday situations',
        'Recall a Hadith related to each value',
        'Demonstrate values through role-play scenarios',
      ],
      videoSlot: 'Animated Islamic values stories with Zaid and friends in a modern Muslim family setting',
      activities: [
        '🤝 Kindness challenge: identify kind actions in illustrated scenarios',
        '📿 Gratitude journal: draw 3 things you are grateful for today',
        '🎭 Role play: act out a scenario requiring honesty',
        '📖 Hadith match: match each Hadith to the value it teaches',
      ],
      quiz: [
        { q: 'The Prophet ﷺ said "The strong person is not the one who overcomes others. The strong person is the one who controls himself when angry." Which value does this teach?', options: ['Honesty', 'Patience and self-control', 'Generosity', 'Courage'], correct: 1, explanation: 'This Hadith teaches patience (صبر) and self-control — true strength is controlling your emotions, not overpowering others.' },
        { q: 'What do we say when we receive a gift or blessing?', options: ['Alhamdulillah', 'Inshallah', 'Mashallah', 'Subhanallah'], correct: 0, explanation: 'We say الحمد لله (Alhamdulillah — All praise is for Allah) to express gratitude for blessings.' },
      ],
      standards: ['UAE MOE Islamic Education Grade 1', 'Saudi MOE Tarbiyah Islamiyah', 'Qatar MOEHE Islamic Studies Foundation'],
      mentorTip: 'Our Prophet Muhammad ﷺ said: "The best among you are those with the best character." Every act of kindness and honesty makes you a better Muslim and a better person!',
    },
    elementary: {
      title: 'Islamic Civilisation: The Golden Age of Islam',
      subtitle: 'Al-Khawarizmi, Ibn Sina, Al-Biruni, and the House of Wisdom',
      objectives: [
        'Identify key scholars of the Islamic Golden Age and their contributions',
        'Explain the role of the House of Wisdom in Baghdad',
        'Analyse how Islamic scholarship preserved and advanced human knowledge',
        'Connect Golden Age discoveries to modern science and mathematics',
      ],
      videoSlot: 'Animated historical journey with Zaid visiting the House of Wisdom in Baghdad',
      activities: [
        '🔢 Al-Khawarizmi\'s algebra: solve simple equations using the method he invented',
        '🏥 Ibn Sina\'s medicine: match symptoms to treatments from the Canon of Medicine',
        '🗺️ Al-Idrisi\'s map: compare his 12th-century world map to a modern map',
        '📜 Timeline: place 8 Golden Age scholars on a chronological timeline',
      ],
      quiz: [
        { q: 'Who is known as the "Father of Algebra"?', options: ['Ibn Sina', 'Al-Khawarizmi', 'Al-Biruni', 'Ibn Rushd'], correct: 1, explanation: 'Muhammad ibn Musa al-Khawarizmi wrote "Al-Kitab al-mukhtasar fi hisab al-jabr wal-muqabala" — the book that gave us the word "algebra" (al-jabr).' },
        { q: 'What was the House of Wisdom (Bayt al-Hikmah)?', options: ['A mosque in Mecca', 'A great library and translation centre in Baghdad', 'A palace of the Caliph', 'A market in Damascus'], correct: 1, explanation: 'The House of Wisdom in Baghdad was the world\'s greatest centre of learning in the 8th–13th centuries, where scholars translated and advanced Greek, Persian, and Indian knowledge.' },
      ],
      standards: ['UAE MOE Social Studies Grade 5', 'Saudi MOE History Grade 5', 'IB PYP Social Studies'],
      mentorTip: 'Muslim scholars kept the light of knowledge burning when much of the world was in darkness. Al-Khawarizmi\'s algebra is in every smartphone today. Your heritage is the foundation of modern science!',
    },
    middle_school: {
      title: 'Quran & Tafsir: Surah Al-Baqarah (Selected Verses)',
      subtitle: 'Meaning, context, and lessons from the longest Surah',
      objectives: [
        'Recite selected verses from Surah Al-Baqarah with correct tajweed',
        'Explain the meaning and context of verses 255 (Ayat al-Kursi) and 285–286',
        'Apply lessons from the Surah to contemporary life situations',
        'Demonstrate understanding of key Quranic vocabulary',
      ],
      videoSlot: 'Tajweed lesson with Zaid reciting and explaining Ayat al-Kursi with animated calligraphy',
      activities: [
        '📿 Tajweed practice: recite Ayat al-Kursi with audio feedback',
        '📖 Tafsir study: read Ibn Kathir\'s commentary on verses 285–286',
        '✏️ Vocabulary builder: learn 10 key Arabic words from the selected verses',
        '🤔 Reflection journal: write how the lessons of Al-Baqarah apply to your life',
      ],
      quiz: [
        { q: 'What is the meaning of "Al-Baqarah"?', options: ['The Cow', 'The Light', 'The Family', 'The Opening'], correct: 0, explanation: 'Al-Baqarah means "The Cow" — named after the story of the cow that the Israelites were commanded to sacrifice (verses 67–73).' },
        { q: 'Ayat al-Kursi (2:255) is known as the greatest verse in the Quran. What does "Kursi" mean?', options: ['Throne/Footstool', 'Light', 'Mercy', 'Knowledge'], correct: 0, explanation: 'Kursi means the Footstool or Throne of Allah, signifying His supreme authority and knowledge over all creation.' },
      ],
      standards: ['UAE MOE Islamic Education Grade 7', 'Saudi MOE Quran Grade 7', 'Al-Azhar Curriculum Middle School'],
      mentorTip: 'The Prophet ﷺ said: "Whoever recites Ayat al-Kursi after every obligatory prayer, nothing stands between him and entering Paradise except death." This verse is your shield!',
    },
    high_school: {
      title: 'Islamic Finance & Economics',
      subtitle: 'Riba, Zakat, Waqf, and the principles of halal finance',
      objectives: [
        'Define and explain the prohibition of riba (interest) in Islamic law',
        'Analyse the mechanisms of Islamic banking instruments (Murabaha, Musharaka, Ijara)',
        'Evaluate the social impact of Zakat and Waqf systems',
        'Compare Islamic and conventional finance models',
      ],
      videoSlot: 'Finance masterclass with Zaid explaining Islamic banking principles with real-world examples',
      activities: [
        '💰 Riba analysis: identify riba in conventional loan scenarios and propose halal alternatives',
        '🏦 Islamic bank simulation: structure a Murabaha home finance deal',
        '📊 Zakat calculator: calculate Zakat on different types of wealth',
        '🕌 Waqf case study: analyse the historical Waqf system and its modern revival',
      ],
      quiz: [
        { q: 'What is riba?', options: ['A type of Islamic charity', 'Interest or usury — prohibited in Islam', 'A form of Islamic partnership', 'A type of Islamic contract'], correct: 1, explanation: 'Riba refers to any unjust increase in exchange or lending, most commonly interest. It is explicitly prohibited in the Quran (2:275–279).' },
        { q: 'In a Murabaha contract, the bank:', options: ['Lends money with interest', 'Buys the asset and sells it to the customer at a disclosed profit', 'Invests in stocks on behalf of the customer', 'Provides a grant to the customer'], correct: 1, explanation: 'In Murabaha, the bank purchases the asset (e.g., a house or car) and sells it to the customer at cost plus a disclosed profit margin — no interest is charged.' },
      ],
      standards: ['UAE MOE Economics Grade 11', 'AAOIFI Standards', 'IB Economics HL Islamic Finance Module'],
      mentorTip: 'Islamic finance is the fastest-growing financial sector in the world, with over $3 trillion in assets. Understanding it is both a religious duty and a career opportunity!',
    },
    college_prep: {
      title: 'Arabic Literature: Classical Poetry & Modern Prose',
      subtitle: 'Al-Mutanabbi, Mahmoud Darwish, and the Arabic literary tradition',
      objectives: [
        'Analyse the poetic techniques of Al-Mutanabbi\'s qasidas',
        'Interpret Mahmoud Darwish\'s poetry as political and cultural expression',
        'Compare classical and modern Arabic literary forms',
        'Produce an original Arabic literary analysis essay',
      ],
      videoSlot: 'Literary analysis seminar with Zaid exploring Arabic poetry from classical to contemporary',
      activities: [
        '📜 Al-Mutanabbi analysis: annotate a qasida for metre, imagery, and themes',
        '🕊️ Darwish close reading: analyse "Identity Card" as a poem of resistance',
        '✏️ Comparative essay: compare classical and modern Arabic poetic voices',
        '🎙️ Poetry recitation: memorise and recite 10 lines of classical Arabic poetry',
      ],
      quiz: [
        { q: 'Al-Mutanabbi is considered one of the greatest Arabic poets. In which century did he live?', options: ['7th century', '10th century', '13th century', '15th century'], correct: 1, explanation: 'Al-Mutanabbi (915–965 CE) lived in the 10th century during the Abbasid period, and is widely regarded as the greatest poet in the Arabic language.' },
        { q: 'Mahmoud Darwish is primarily associated with:', options: ['Egyptian nationalist poetry', 'Palestinian identity and resistance poetry', 'Gulf modernist poetry', 'Lebanese romantic poetry'], correct: 1, explanation: 'Mahmoud Darwish (1941–2008) is the national poet of Palestine, whose work explores exile, identity, and resistance.' },
      ],
      standards: ['UAE MOE Arabic Literature Grade 12', 'IB Language A Arabic HL', 'Al-Azhar University Entrance Arabic'],
      mentorTip: 'Arabic has the richest poetic tradition in the world — 1,400 years of unbroken literary excellence. When you read Al-Mutanabbi, you are reading the pinnacle of human expression.',
    },
  },
  oceania: {
    early_childhood: {
      title: 'Ocean Animals & Habitats',
      subtitle: 'Marine science through Pacific and Australian ocean life',
      objectives: [
        'Name 6 ocean animals found in the Pacific and Indian Oceans',
        'Describe basic features of ocean habitats',
        'Distinguish between ocean and land animals',
        'Develop vocabulary for ocean environments',
      ],
      videoSlot: 'Animated underwater adventure with Tane exploring the Great Barrier Reef',
      activities: [
        '🐠 Reef explorer: click on fish and coral to learn their names',
        '🐋 Animal sort: sort animals into ocean and land groups',
        '🦈 Shark facts: match shark species to their characteristics',
        '🎨 Colour the reef: colour a Great Barrier Reef scene with correct colours',
      ],
      quiz: [
        { q: 'Where do clownfish live?', options: ['In rivers', 'In coral reefs', 'In the desert', 'In forests'], correct: 1, explanation: 'Clownfish live in coral reefs, often in sea anemones. The Great Barrier Reef is home to many clownfish!' },
        { q: 'Which is the largest ocean animal?', options: ['Shark', 'Dolphin', 'Blue whale', 'Octopus'], correct: 2, explanation: 'The blue whale is the largest animal on Earth — it can grow up to 30 metres long and weigh 200 tonnes!' },
      ],
      standards: ['Australian EYLF Learning Outcome 2', 'NZ Te Whāriki Exploration Strand', 'ACARA Foundation Science'],
      mentorTip: 'The Pacific Ocean is the largest ocean on Earth, covering more than 30% of the planet\'s surface! Tane\'s ancestors navigated it using only stars and waves. Amazing!',
    },
    foundation: {
      title: 'Indigenous Australian Stories & Country',
      subtitle: 'Learning through Aboriginal and Torres Strait Islander perspectives',
      objectives: [
        'Understand the concept of "Country" in Aboriginal culture',
        'Identify 3 Dreamtime stories and their teachings',
        'Recognise Aboriginal art symbols and their meanings',
        'Develop respect for Indigenous Australian knowledge systems',
      ],
      videoSlot: 'Animated Dreamtime story with Tane and an Aboriginal elder sharing stories of Country',
      activities: [
        '🌏 Country map: identify different Aboriginal nations on a map of Australia',
        '🎨 Dot painting: create an Aboriginal dot painting using traditional symbols',
        '📖 Dreamtime story: listen to and retell a Dreamtime story',
        '🌿 Bush tucker: identify traditional Aboriginal food plants',
      ],
      quiz: [
        { q: 'What does "Country" mean to Aboriginal Australians?', options: ['Just the land', 'A deep spiritual connection to land, water, sky, and all living things', 'A political territory', 'A farming area'], correct: 1, explanation: '"Country" for Aboriginal Australians encompasses land, water, sky, and all living things, along with the spiritual connections, stories, and responsibilities that bind people to place.' },
        { q: 'What are Dreamtime stories?', options: ['Bedtime stories for children', 'Sacred stories explaining the creation of the world and laws for living', 'Historical records', 'Entertainment only'], correct: 1, explanation: 'Dreamtime stories are sacred narratives that explain how the world was created, establish laws for living, and connect people to Country.' },
      ],
      standards: ['ACARA Foundation HASS', 'Australian Curriculum Cross-Curriculum Priority: Aboriginal and Torres Strait Islander Histories and Cultures', 'NZ Curriculum Social Sciences'],
      mentorTip: 'Aboriginal Australians have the world\'s oldest continuous culture — over 65,000 years! Their knowledge of Country, plants, and stars is one of humanity\'s greatest treasures.',
    },
    elementary: {
      title: 'Pacific Navigation: Stars, Waves & Wayfinding',
      subtitle: 'Traditional Polynesian navigation and modern applications',
      objectives: [
        'Explain how Polynesian navigators used stars, waves, and birds to navigate',
        'Identify 5 major stars used in Pacific navigation',
        'Describe the construction and design of traditional waka/canoes',
        'Connect traditional navigation to modern GPS and satellite technology',
      ],
      videoSlot: 'Animated Pacific voyage with Tane navigating by stars across the Pacific Ocean',
      activities: [
        '⭐ Star chart: identify Polaris, Southern Cross, and 3 other navigation stars',
        '🌊 Wave reading: simulate reading wave patterns to determine direction',
        '🛶 Canoe builder: design a traditional waka with correct structural features',
        '🗺️ Voyage map: trace traditional Polynesian migration routes across the Pacific',
      ],
      quiz: [
        { q: 'How did Polynesian navigators know they were approaching land?', options: ['They used compasses', 'They observed birds, clouds, and wave patterns', 'They used maps', 'They followed the sun only'], correct: 1, explanation: 'Polynesian navigators read nature: birds fly toward land at dusk, clouds form over islands, and wave patterns change near land masses.' },
        { q: 'What is the Southern Cross used for in navigation?', options: ['Telling the time', 'Finding south in the Southern Hemisphere', 'Predicting weather', 'Measuring distance'], correct: 1, explanation: 'The Southern Cross (Crux) points toward the South Celestial Pole, helping navigators in the Southern Hemisphere find south.' },
      ],
      standards: ['ACARA Science Grade 5', 'NZ Curriculum Science Level 3', 'Pacific Regional Education Framework'],
      mentorTip: 'Polynesian navigators sailed 5,000 kilometres across open ocean without instruments — using only their senses and knowledge of nature. They were the greatest navigators in human history!',
    },
    middle_school: {
      title: 'Climate Change & the Pacific: Science & Advocacy',
      subtitle: 'Climate science, Pacific vulnerability, and youth activism',
      objectives: [
        'Explain the greenhouse effect and its relationship to climate change',
        'Analyse climate data showing temperature and sea level trends',
        'Evaluate the specific vulnerability of Pacific Island nations to climate change',
        'Develop a climate advocacy argument using scientific evidence',
      ],
      videoSlot: 'Documentary-style climate science lesson with Tane visiting a Pacific island community',
      activities: [
        '📊 Climate data analysis: interpret temperature anomaly graphs from 1880–2024',
        '🌊 Sea level simulation: model the impact of 1m sea level rise on Pacific islands',
        '📝 Advocacy letter: write a letter to a world leader about Pacific climate justice',
        '🔬 Carbon cycle diagram: label and explain the carbon cycle',
      ],
      quiz: [
        { q: 'Why are Pacific Island nations particularly vulnerable to climate change?', options: ['They have no technology', 'They are low-lying and surrounded by rising seas', 'They produce the most emissions', 'They have no government'], correct: 1, explanation: 'Many Pacific islands are only 1–2 metres above sea level. Rising seas from climate change threaten to submerge entire nations like Tuvalu and Kiribati.' },
        { q: 'What is the main cause of current climate change?', options: ['Volcanic eruptions', 'Human greenhouse gas emissions', 'Changes in the Sun\'s output', 'Natural climate cycles'], correct: 1, explanation: 'The scientific consensus is that current climate change is primarily caused by human emissions of greenhouse gases, especially CO₂ from burning fossil fuels.' },
      ],
      standards: ['ACARA Science Grade 8', 'NZ Curriculum Science Level 4', 'Australian Curriculum Cross-Curriculum Priority: Sustainability'],
      mentorTip: 'Pacific youth are leading the world in climate activism. Voices like those from Fiji and Tuvalu are changing global policy. Science + advocacy = change!',
    },
    high_school: {
      title: 'Australian & New Zealand Literature: Identity & Place',
      subtitle: 'Tim Winton, Janet Frame, and postcolonial Pacific voices',
      objectives: [
        'Analyse how Australian and NZ literature constructs national identity',
        'Apply postcolonial theory to selected texts',
        'Evaluate the representation of landscape and place in Pacific literature',
        'Produce a comparative literary analysis at senior secondary standard',
      ],
      videoSlot: 'Literary analysis seminar with Tane exploring Australian and Pacific literature',
      activities: [
        '📖 Close reading: annotate a passage from Tim Winton\'s "Cloudstreet" for place and identity',
        '🌿 Maori literature: analyse a waiata (song) as literary text',
        '✏️ Comparative essay: compare representations of land in two Pacific texts',
        '🎙️ Oral presentation: present a 5-minute analysis of a chosen Pacific author',
      ],
      quiz: [
        { q: 'What is a central theme in Australian literature?', options: ['Urban sophistication', 'The relationship between people and the harsh Australian landscape', 'European nostalgia', 'Industrial progress'], correct: 1, explanation: 'Australian literature frequently explores the complex, often harsh relationship between settlers and Indigenous peoples with the Australian landscape.' },
        { q: 'What is the significance of the Maori concept of "whenua" in NZ literature?', options: ['It means the sky', 'It means land/placenta — connecting people to their origins and identity', 'It means the sea', 'It means community'], correct: 1, explanation: 'Whenua means both "land" and "placenta" in Maori — symbolising the deep connection between people and the land that nourishes and defines them.' },
      ],
      standards: ['ACARA English Year 11–12', 'NCEA Level 3 English', 'IB Language A Literature HL'],
      mentorTip: 'Pacific literature is young and powerful. Writers like Patricia Grace, Albert Wendt, and Tim Winton are telling stories the world needs to hear. Your voice matters too!',
    },
    college_prep: {
      title: 'Environmental Science: Biodiversity & Conservation Biology',
      subtitle: 'Species extinction, ecosystem services, and conservation strategies',
      objectives: [
        'Define biodiversity and explain its importance at genetic, species, and ecosystem levels',
        'Analyse the IUCN Red List data for Oceanian species',
        'Evaluate the effectiveness of different conservation strategies',
        'Design a conservation management plan for a threatened Oceanian species',
      ],
      videoSlot: 'Field research documentary with Tane studying endangered species in Oceania',
      activities: [
        '📊 IUCN data analysis: analyse Red List data for 10 Oceanian species',
        '🌿 Ecosystem services mapping: identify and value services provided by the Great Barrier Reef',
        '🐦 Conservation plan: design a recovery plan for the kakapo (NZ\'s flightless parrot)',
        '📝 Research essay: evaluate the effectiveness of marine protected areas in Oceania',
      ],
      quiz: [
        { q: 'What are ecosystem services?', options: ['Services provided by conservation organisations', 'Benefits that humans receive from ecosystems', 'Government environmental programs', 'Wildlife tourism activities'], correct: 1, explanation: 'Ecosystem services are the benefits humans receive from ecosystems, including clean water, clean air, food, climate regulation, and cultural values.' },
        { q: 'What does IUCN stand for?', options: ['International Union for Conservation of Nature', 'International University of Conservation and Nature', 'Institute for United Conservation Networks', 'International Union of Climate and Nature'], correct: 0, explanation: 'IUCN stands for the International Union for Conservation of Nature — the global authority on the status of the natural world.' },
      ],
      standards: ['ACARA Environmental Science Year 12', 'NCEA Level 3 Biology', 'IB Environmental Systems & Societies HL'],
      mentorTip: 'Oceania has some of the world\'s most unique biodiversity — species found nowhere else on Earth. Protecting them is protecting our planet\'s irreplaceable heritage.',
    },
  },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function MentorCharacter({ region, mentorName }: { region: Region; mentorName: string }) {
  const cfg = REGIONS[region];
  return (
    <div className={`relative flex flex-col items-center ${cfg.mentorBg} rounded-2xl p-4 border border-border`}>
      <div className="relative w-28 h-28 mb-2">
        <Image
          src={cfg.mentorSvg}
          alt={`${mentorName} — cartoon mentor character for ${cfg.label} region`}
          fill
          className="object-contain drop-shadow-md"
          style={{ animation: 'mentorBob 3s ease-in-out infinite' }}
        />
      </div>
      <p className="text-sm font-700 text-foreground">{mentorName}</p>
      <p className={`text-xs font-500 ${cfg.color}`}>{cfg.label} Guide</p>
    </div>
  );
}

function VideoSlot({ description }: { description: string }) {
  return (
    <div className="relative w-full aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden border border-border flex flex-col items-center justify-center gap-3 group cursor-pointer hover:from-slate-700 hover:to-slate-800 transition-all">
      <div className="w-16 h-16 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <Icon name="PlayIcon" size={28} className="text-white ml-1" />
      </div>
      <div className="text-center px-6">
        <p className="text-white font-600 text-sm">Lesson Video</p>
        <p className="text-white/60 text-xs mt-1 max-w-xs">{description}</p>
      </div>
      <div className="absolute top-3 right-3 bg-white/10 text-white text-xs px-2 py-1 rounded-full border border-white/20">
        Video Coming Soon
      </div>
    </div>
  );
}

function ObjectivesList({ objectives }: { objectives: string[] }) {
  return (
    <div className="space-y-2">
      {objectives.map((obj, i) => (
        <div key={i} className="flex items-start gap-3 p-3 bg-muted/50 rounded-xl">
          <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-700 flex-shrink-0 mt-0.5">
            {i + 1}
          </div>
          <p className="text-sm text-foreground/80 leading-relaxed">{obj}</p>
        </div>
      ))}
    </div>
  );
}

function ActivityCard({ activity, index }: { activity: string; index: number }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => setDone(!done)}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
        done ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
          done ? 'border-primary bg-primary' : 'border-muted-foreground/40'
        }`}>
          {done && <Icon name="CheckIcon" size={12} className="text-white" />}
        </div>
        <p className={`text-sm leading-relaxed ${done ? 'line-through text-muted-foreground' : 'text-foreground/80'}`}>
          {activity}
        </p>
      </div>
    </button>
  );
}

function QuizSection({ quiz }: { quiz: QuizQ[] }) {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});

  function handleAnswer(qi: number, oi: number) {
    if (revealed[qi]) return;
    setAnswers((prev) => ({ ...prev, [qi]: oi }));
  }

  function reveal(qi: number) {
    if (answers[qi] === undefined) return;
    setRevealed((prev) => ({ ...prev, [qi]: true }));
  }

  const score = Object.keys(revealed).filter((k) => answers[Number(k)] === quiz[Number(k)].correct).length;

  return (
    <div className="space-y-6">
      {quiz.map((q, qi) => (
        <div key={qi} className="bg-card border border-border rounded-2xl p-5">
          <p className="font-600 text-foreground mb-4 text-sm leading-relaxed">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-white text-xs font-700 mr-2">{qi + 1}</span>
            {q.q}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            {q.options.map((opt, oi) => {
              const selected = answers[qi] === oi;
              const isCorrect = oi === q.correct;
              let cls = 'border-border bg-muted/30 hover:border-primary/40 hover:bg-muted/60';
              if (revealed[qi]) {
                if (isCorrect) cls = 'border-green-500 bg-green-50 text-green-800';
                else if (selected) cls = 'border-red-400 bg-red-50 text-red-700';
              } else if (selected) {
                cls = 'border-primary bg-primary/10 text-primary';
              }
              return (
                <button
                  key={oi}
                  onClick={() => handleAnswer(qi, oi)}
                  className={`text-left px-4 py-3 rounded-xl border-2 text-sm font-500 transition-all duration-150 ${cls}`}
                >
                  <span className="font-700 mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                </button>
              );
            })}
          </div>
          {!revealed[qi] ? (
            <button
              onClick={() => reveal(qi)}
              disabled={answers[qi] === undefined}
              className="text-xs font-600 text-primary disabled:text-muted-foreground disabled:cursor-not-allowed hover:underline"
            >
              Check Answer
            </button>
          ) : (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-800 leading-relaxed">
              <span className="font-700">Explanation: </span>{q.explanation}
            </div>
          )}
        </div>
      ))}
      {Object.keys(revealed).length === quiz.length && (
        <div className="p-4 bg-gradient-to-r from-primary/10 to-teal-500/10 border border-primary/20 rounded-2xl text-center">
          <p className="font-700 text-foreground text-lg">Quiz Complete! 🎉</p>
          <p className="text-muted-foreground text-sm mt-1">You scored <span className="text-primary font-700">{score}/{quiz.length}</span></p>
        </div>
      )}
    </div>
  );
}

function StandardsTag({ standard }: { standard: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted border border-border rounded-full text-xs font-500 text-foreground/70">
      <Icon name="AcademicCapIcon" size={12} className="text-primary flex-shrink-0" />
      {standard}
    </span>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LessonPlayerPage() {
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [activeTab, setActiveTab] = useState<'objectives' | 'activities' | 'quiz' | 'standards'>('objectives');

  const regionCfg = selectedRegion ? REGIONS[selectedRegion] : null;
  const lesson = selectedRegion && selectedLevel ? CURRICULUM[selectedRegion][selectedLevel] : null;
  const levelCfg = selectedLevel ? LEVELS[selectedLevel] : null;

  // Reset tab when lesson changes
  function selectRegion(r: Region) {
    setSelectedRegion(r);
    setSelectedLevel(null);
    setActiveTab('objectives');
  }

  function selectLevel(l: Level) {
    setSelectedLevel(l);
    setActiveTab('objectives');
  }

  return (
    <AppLayout activeRoute="/lesson-player">
      <style>{`
        @keyframes mentorBob {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>

      {/* ── Header ── */}
      <div className="bg-gradient-to-br from-primary via-violet-600 to-teal-500 rounded-3xl px-6 py-8 text-white mb-8">
        <div className="max-w-4xl">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎓</span>
            <span className="text-xs font-600 bg-white/20 px-3 py-1 rounded-full">DOE-Aligned Curriculum</span>
          </div>
          <h1 className="text-2xl font-800 tracking-tight mb-1">Global Lesson Player</h1>
          <p className="text-white/80 text-sm max-w-xl">
            Select your region and developmental level to access curriculum-aligned lessons with your personal cartoon mentor, interactive activities, and standards-tagged content.
          </p>
          {selectedRegion && selectedLevel && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="bg-white/20 text-white text-xs font-600 px-3 py-1.5 rounded-full">
                {regionCfg?.flag} {regionCfg?.label}
              </span>
              <span className="bg-white/20 text-white text-xs font-600 px-3 py-1.5 rounded-full">
                {levelCfg?.emoji} {levelCfg?.label} · {levelCfg?.ageRange}
              </span>
              <span className="bg-white/20 text-white text-xs font-600 px-3 py-1.5 rounded-full">
                📋 {regionCfg?.standardsBody}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* ── Left Panel: Region + Level Selector ── */}
        <div className="xl:col-span-1 space-y-6">
          {/* Region Selector */}
          <div className="bg-card border border-border rounded-2xl p-4">
            <h2 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
              <Icon name="GlobeAltIcon" size={16} className="text-primary" />
              Select Your Region
            </h2>
            <div className="space-y-2">
              {(Object.keys(REGIONS) as Region[]).map((r) => {
                const cfg = REGIONS[r];
                const isActive = selectedRegion === r;
                return (
                  <button
                    key={r}
                    onClick={() => selectRegion(r)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl border-2 transition-all duration-150 flex items-center gap-3 ${
                      isActive
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/30'
                    }`}
                  >
                    <span className="text-lg">{cfg.flag}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-600 truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>{cfg.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{cfg.mentorName}</p>
                    </div>
                    {isActive && <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Level Selector */}
          {selectedRegion && (
            <div className="bg-card border border-border rounded-2xl p-4">
              <h2 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                <Icon name="AcademicCapIcon" size={16} className="text-primary" />
                Developmental Level
              </h2>
              <div className="space-y-2">
                {(Object.keys(LEVELS) as Level[]).map((l) => {
                  const cfg = LEVELS[l];
                  const isActive = selectedLevel === l;
                  return (
                    <button
                      key={l}
                      onClick={() => selectLevel(l)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl border-2 transition-all duration-150 ${
                        isActive
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-base">{cfg.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-600 truncate ${isActive ? 'text-primary' : 'text-foreground'}`}>{cfg.label}</p>
                          <p className="text-xs text-muted-foreground">{cfg.ageRange}</p>
                        </div>
                        {isActive && <Icon name="CheckCircleIcon" size={14} className="text-primary flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mentor Character */}
          {selectedRegion && (
            <MentorCharacter region={selectedRegion} mentorName={REGIONS[selectedRegion].mentorName} />
          )}
        </div>

        {/* ── Right Panel: Lesson Content ── */}
        <div className="xl:col-span-3">
          {!selectedRegion ? (
            <div className="bg-card border border-border rounded-2xl p-12 flex flex-col items-center justify-center text-center min-h-96">
              <span className="text-5xl mb-4">🌍</span>
              <h3 className="text-lg font-700 text-foreground mb-2">Choose Your Region</h3>
              <p className="text-muted-foreground text-sm max-w-sm">
                Select your continent or region from the left panel to meet your personal cartoon mentor and access curriculum-aligned lessons.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-6">
                {(Object.keys(REGIONS) as Region[]).map((r) => (
                  <button
                    key={r}
                    onClick={() => selectRegion(r)}
                    className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/40 rounded-full text-sm font-500 transition-all"
                  >
                    <span>{REGIONS[r].flag}</span>
                    {REGIONS[r].label}
                  </button>
                ))}
              </div>
            </div>
          ) : !selectedLevel ? (
            <div className="bg-card border border-border rounded-2xl p-8 flex flex-col items-center justify-center text-center min-h-96">
              <span className="text-4xl mb-3">{regionCfg?.flag}</span>
              <h3 className="text-lg font-700 text-foreground mb-1">Welcome to {regionCfg?.label}!</h3>
              <p className="text-muted-foreground text-sm mb-1">Your mentor is <span className="font-600 text-foreground">{regionCfg?.mentorName}</span></p>
              <p className="text-xs text-muted-foreground mb-6">Standards: {regionCfg?.standardsBody}</p>
              <p className="text-sm text-muted-foreground">Now select your developmental level from the left panel to begin your lesson.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6 w-full max-w-lg">
                {(Object.keys(LEVELS) as Level[]).map((l) => {
                  const cfg = LEVELS[l];
                  return (
                    <button
                      key={l}
                      onClick={() => selectLevel(l)}
                      className={`p-3 rounded-xl border-2 border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center`}
                    >
                      <span className="text-2xl block mb-1">{cfg.emoji}</span>
                      <p className="text-xs font-600 text-foreground">{cfg.label}</p>
                      <p className="text-xs text-muted-foreground">{cfg.ageRange}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : lesson ? (
            <div className="space-y-6">
              {/* Lesson Header */}
              <div className={`bg-gradient-to-br ${regionCfg?.gradient} rounded-2xl p-6 text-white`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-600 bg-white/20 px-2 py-1 rounded-full`}>{levelCfg?.emoji} {levelCfg?.label}</span>
                      <span className="text-xs font-600 bg-white/20 px-2 py-1 rounded-full">{regionCfg?.flag} {regionCfg?.label}</span>
                    </div>
                    <h2 className="text-xl font-800 mb-1">{lesson.title}</h2>
                    <p className="text-white/80 text-sm">{lesson.subtitle}</p>
                  </div>
                </div>
              </div>

              {/* Video Slot */}
              <VideoSlot description={lesson.videoSlot} />

              {/* Mentor Tip */}
              <div className={`${regionCfg?.mentorBg} border border-border rounded-2xl p-4 flex items-start gap-4`}>
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="ChatBubbleLeftEllipsisIcon" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-xs font-700 text-primary mb-1">{regionCfg?.mentorName} says:</p>
                  <p className="text-sm text-foreground/80 leading-relaxed italic">"{lesson.mentorTip}"</p>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="flex border-b border-border overflow-x-auto">
                  {(['objectives', 'activities', 'quiz', 'standards'] as const).map((tab) => {
                    const labels: Record<string, string> = {
                      objectives: '🎯 Objectives',
                      activities: '🎮 Activities',
                      quiz: '📝 Quiz',
                      standards: '📋 Standards',
                    };
                    return (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-5 py-3.5 text-sm font-600 whitespace-nowrap transition-all border-b-2 ${
                          activeTab === tab
                            ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
                        }`}
                      >
                        {labels[tab]}
                      </button>
                    );
                  })}
                </div>

                <div className="p-5">
                  {activeTab === 'objectives' && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-4 font-500">By the end of this lesson, learners will be able to:</p>
                      <ObjectivesList objectives={lesson.objectives} />
                    </div>
                  )}

                  {activeTab === 'activities' && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-4 font-500">Complete these activities to reinforce your learning:</p>
                      <div className="space-y-3">
                        {lesson.activities.map((act, i) => (
                          <ActivityCard key={i} activity={act} index={i} />
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'quiz' && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-4 font-500">Test your understanding — select an answer then click "Check Answer":</p>
                      <QuizSection quiz={lesson.quiz} />
                    </div>
                  )}

                  {activeTab === 'standards' && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-4 font-500">This lesson is aligned to the following educational standards:</p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {lesson.standards.map((s, i) => (
                          <StandardsTag key={i} standard={s} />
                        ))}
                      </div>
                      <div className="bg-muted/50 rounded-xl p-4 border border-border">
                        <p className="text-xs font-700 text-foreground mb-2">Standards Body</p>
                        <p className="text-sm text-muted-foreground">{regionCfg?.standardsBody}</p>
                        <p className="text-xs text-muted-foreground mt-2">{regionCfg?.countries}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </AppLayout>
  );
}
